import { FC, useMemo } from 'react';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import LoadingPage from '../../../shared/LoadingPage/LoadingPage';
import { ITrainingBlock } from '../../../models/allenamenti';
import { DAYS, pluralizeIt, readErrorMessage, toIsoDate } from '../allenamenti.constants';
import { useAllenamentiTaxonomy } from '../hooks/useAllenamentiTaxonomy';
import { ITeamDailyLoadRecovery } from './useTeamDailyLoadRecovery';
import DayColumn from './DayColumn';
import { BlockCardBody } from './BlockCard';
import { useWeekBoardDnd } from './useWeekBoardDnd';
import { WEEK_BOARD_SCREEN_READER_INSTRUCTIONS, buildWeekBoardAnnouncements } from './weekBoardAnnouncements';
import blockCardStyles from './BlockCard.module.scss';
import styles from './WeekBoard.module.scss';

interface IWeekBoardProps {
  weekLabel: string;
  weekDays: { date: Date; iso: string }[];
  blocks: ITrainingBlock[];
  blocksByDay: Map<string, ITrainingBlock[]>;
  totalMinutes: number;
  totalBlocks: number;
  trainingDays: number;
  dominantCategory: { categoryId: string; percent: number | null } | null;
  /** RPE medio e recupero medio squadra per giorno, chiave = iso del giorno. */
  dailyLoadByDate: Map<string, ITeamDailyLoadRecovery>;
  isLoading: boolean;
  /** `true` se il caricamento della settimana è fallito (es. rate limit). */
  isError: boolean;
  error: unknown;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onAddBlock: (iso: string) => void;
  onEditBlock: (block: ITrainingBlock) => void;
  /** Incolla il contenuto degli appunti in fondo al giorno `iso` (F1). */
  onPasteBlock: (iso: string) => void;
  /**
   * `true` mentre la creazione innescata da "Incolla" è in volo (F1,
   * hardening double-submit): disabilita il bottone in ogni colonna, non solo
   * in quella cliccata — la scrittura è comunque unica alla volta.
   */
  isPastingBlock: boolean;
  /** Riprova la GET fallita: la stessa `refetch` esposta dalla query. */
  onRetry: () => void;
  /** Drag-and-drop: verticale dentro il giorno, diagonale fra due giorni. */
  /** Persiste la nuova sequenza dei giorni toccati dal trascinamento. */
  onReorder: (body: { days: { localDate: string; blockIds: string[] }[] }) => void;
}

/** Griglia della settimana: intestazione con i totali e sette colonne. */
const WeekBoard: FC<IWeekBoardProps> = ({
  weekLabel,
  weekDays,
  blocks,
  blocksByDay,
  totalMinutes,
  totalBlocks,
  trainingDays,
  dominantCategory,
  dailyLoadByDate,
  isLoading,
  isError,
  error,
  onPrevWeek,
  onNextWeek,
  onAddBlock,
  onEditBlock,
  onPasteBlock,
  isPastingBlock,
  onRetry,
  onReorder,
}) => {
  const { bySlug } = useAllenamentiTaxonomy();
  const resolve = (slug: string) => bySlug.get(slug);

  const {
    sensors,
    activeBlock,
    registerCardNode,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useWeekBoardDnd({
    blocks,
    dayIsos: weekDays.map((d) => d.iso),
    onReorder,
  });

  // La settimana mostrata potrebbe non contenere affatto oggi (navigazione
  // avanti/indietro): in quel caso nessuna colonna va marcata.
  const todayIso = useMemo(() => toIsoDate(new Date()), []);

  // Nome italiano del giorno per ogni iso della settimana mostrata: DAYS e
  // weekDays sono nello stesso ordine (entrambi lunedì→domenica), quindi si
  // zippano per indice — stessa assunzione già fatta poco sotto nel render
  // della griglia.
  const dayLabelByIso = useMemo(() => {
    const map = new Map<string, string>();
    DAYS.forEach((day, i) => map.set(weekDays[i].iso, day.label));
    return map;
  }, [weekDays]);

  const announcements = useMemo(
    () => buildWeekBoardAnnouncements(blocks, dayLabelByIso),
    [blocks, dayLabelByIso],
  );

  const activeIndex = useMemo(() => {
    if (!activeBlock) return 0;
    const dayBlocks = blocksByDay.get(activeBlock.localDate) ?? [];
    const i = dayBlocks.findIndex((b) => b._id === activeBlock._id);
    return i >= 0 ? i : 0;
  }, [activeBlock, blocksByDay]);

  const dominant = dominantCategory ? resolve(dominantCategory.categoryId) : undefined;

  return (
    <div className={styles.board}>
      <div className={styles.toolbar}>
        <div className={styles.nav}>
          <CustomButton
            size="sm"
            color="outline"
            onClick={onPrevWeek}
            icon={<CaretLeftIcon size={16} weight="bold" />}
            iconPosition="left"
            ariaLabel="Settimana precedente"
          />
          <span className={styles.weekLabel}>{weekLabel}</span>
          <CustomButton
            size="sm"
            color="outline"
            onClick={onNextWeek}
            icon={<CaretRightIcon size={16} weight="bold" />}
            iconPosition="left"
            ariaLabel="Settimana successiva"
          />
        </div>

        {/* Gerarchia: i minuti totali sono il titolo, blocchi/giorni/categoria
            dominante sono contesto — differenziati per dimensione e peso,
            non per colore. */}
        <div className={styles.stats}>
          <div className={styles.statHeadline}>
            <strong className={styles.statValue}>{totalMinutes}</strong>
            <span className={styles.statUnit}>
              {pluralizeIt(totalMinutes, 'minuto', 'minuti')}
            </span>
          </div>

          <div className={styles.statSecondary}>
            <span>
              {totalBlocks} {pluralizeIt(totalBlocks, 'blocco', 'blocchi')}
            </span>
            <span className={styles.statDot}>·</span>
            <span>
              {trainingDays} {pluralizeIt(trainingDays, 'giorno', 'giorni')}
            </span>
            {dominant && dominantCategory && (
              <>
                <span className={styles.statDot}>·</span>
                <span className={styles.dominant}>
                  <span
                    className={styles.dominantDot}
                    style={{ background: dominant.color }}
                  />
                  {dominant.labelIt}
                  {dominantCategory.percent != null ? ` ${dominantCategory.percent}%` : ''}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.skeleton}>
          <LoadingPage />
        </div>
      ) : isError ? (
        <div className={styles.errorState}>
          <p className={styles.errorText}>{readErrorMessage(error)}</p>
          <CustomButton size="sm" color="outline" onClick={onRetry}>
            Riprova
          </CustomButton>
        </div>
      ) : (
        <>
          {totalBlocks === 0 && (
            <p className={styles.emptyWeekHint}>
              Settimana senza blocchi pianificati — aggiungi il primo da un giorno qui sotto.
            </p>
          )}

          {/*
            `closestCenter` è obbligatorio, non una preferenza. Il default di
            dnd-kit è `rectIntersection`, che sceglie la zona con l'area di
            sovrapposizione maggiore: la colonna contiene le card ed è molto
            più grande, quindi vinceva sempre lei e il rilascio SOPRA una card
            non veniva mai riconosciuto — il riordino verticale era inerte.
            Confrontando le distanze fra centri, la card sotto il cursore batte
            la colonna, e sullo spazio vuoto resta la colonna, che è ciò che
            serve per il giorno senza blocchi.
          */}
          <DndContext
            collisionDetection={closestCenter}
            sensors={sensors}
            accessibility={{
              announcements,
              screenReaderInstructions: WEEK_BOARD_SCREEN_READER_INSTRUCTIONS,
            }}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className={styles.grid}>
              {DAYS.map((day, i) => (
                <DayColumn
                  key={day.key}
                  short={day.short}
                  dayNumber={weekDays[i].date.getDate()}
                  iso={weekDays[i].iso}
                  blocks={blocksByDay.get(weekDays[i].iso) ?? []}
                  dailyLoad={dailyLoadByDate.get(weekDays[i].iso)}
                  isToday={weekDays[i].iso === todayIso}
                  resolve={resolve}
                  onAdd={onAddBlock}
                  onEdit={onEditBlock}
                  onPaste={onPasteBlock}
                  isPasting={isPastingBlock}
                  registerCardNode={registerCardNode}
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeBlock ? (
                <div
                  className={`${blockCardStyles.card} ${blockCardStyles.dragOverlayCard}`}
                  style={{
                    borderLeftColor: resolve(activeBlock.categoryIds?.[0] ?? '')
                      ?.color,
                  }}
                >
                  <BlockCardBody
                    block={activeBlock}
                    index={activeIndex}
                    categories={(activeBlock.categoryIds ?? []).map((id) => ({
                      label: resolve(id)?.labelIt ?? id,
                      color: resolve(id)?.color,
                    }))}
                    subcategoryLabels={(activeBlock.subcategoryIds ?? []).map(
                      (id) => resolve(id)?.labelIt ?? id,
                    )}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </>
      )}
    </div>
  );
};

export default WeekBoard;
